import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Alert,
  AlertTitle,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import programmingContentService from "app/services/programming-content-service";
import { Panel, PanelGroup } from "react-resizable-panels";
import JumboConfirmDialog from "@jumbo/components/JumboConfirmDialog";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import StyledPanelResizeHandle from "components/StyledPanelResizeHandle/StyledPanelResizeHandle";
import TerminalIcon from "@mui/icons-material/Terminal";
import InputIcon from "@mui/icons-material/Input";
import useMetadata from "app/hooks/useMetadata";
import CodeIcon from "@mui/icons-material/Code";
import codeConstants from "app/utils/constants/codeConstants";
import EditorControls from "app/pages/ProblemDetail/CodeTestPanel/EditorControls";
import useAdmin from "app/routes/middleware/Admin/useAdmin";
import {
  ROUTES,
  BASE_PATHS,
  SUB_PATHS,
} from "app/utils/constants/routeConstants";
import Div from "@jumbo/shared/Div";
import ContentPanel from "./ContentPanel";
import EditorPanel from "./EditorPanel";
import MobileEditorPanel from "./MobileEditorPanel";
import ContentHeader from "components/ContentHeader/ContentHeader";
import {
  ALERT_SEVERITIES,
  LOADING_MESSAGES,
  SUBMISSION_STATUSES,
} from "app/utils/constants/constants";
import LoadingView from "components/LoadingView";
import ErrorView from "components/ErrorView";

const ProgrammingContentPage = () => {
  const { url } = useParams();
  const navigate = useNavigate();
  const isAdmin = useAdmin();
  const { metadata } = useMetadata();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [code, setCode] = useState("");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [codeTabValue, setCodeTabValue] = useState(0);
  const [ioTabValue, setIOTabValue] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editorLanguage, setEditorLanguage] = useState("java");
  const [isPolling, setIsPolling] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState("Compiling...");
  const pollingRef = useRef(null);
  const isResetting = useRef(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await programmingContentService.getContentByUrl(url);
        setContent(data);
        const activeDetails = data.contentDetails.filter((d) => d.active);
        if (
          activeDetails.length > 0 &&
          activeDetails[0].problemCodeSnippets.length > 0
        ) {
          const firstSnippet = activeDetails[0].problemCodeSnippets[0];
          const languageKey = firstSnippet.programmingLanguage;
          const config = codeConstants[languageKey];
          setCode(config?.defaultCodesnippet || "");
          setEditorLanguage(config?.language || "text");
        }
      } catch (err) {
        setError("Failed to load content.");
      } finally {
        setLoading(false);
      }
    };
    if (url) fetchContent();
  }, [url]);

  useEffect(() => {
    if (!isPolling) return;
    const messages = LOADING_MESSAGES;
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setLoadingMessage(messages[index]);
    }, 2000);
    return () => clearInterval(interval);
  }, [isPolling]);

  const handleRun = async () => {
    setIsRunning(true);
    setOutput("");
    setExecutionResult(null);

    const activeDetails =
      content?.contentDetails.filter((detail) => detail.active) || [];
    let languageKey = "JAVA";
    if (
      activeDetails.length > 0 &&
      activeDetails[0].problemCodeSnippets.length > 0
    ) {
      languageKey = activeDetails[0].problemCodeSnippets[0].programmingLanguage;
    }

    const compilerRequest = {
      sourceCode: code,
      programmingLanguage: languageKey,
      executionInput: input,
      executeParallely: true,
    };

    try {
      const response = await programmingContentService.executeCode(
        compilerRequest
      );
      const { uniqueToken } = response;
      setIsPolling(true);
      setIOTabValue(1);
      pollRunStatus(uniqueToken);
    } catch (error) {
      console.error("Code execution error:", error);
      setOutput(`Error: ${error.message}`);
      setIsPolling(false);
      setIsRunning(false);
    }
  };

  const pollRunStatus = async (submissionId) => {
    const checkStatus = async () => {
      try {
        const result =
          await programmingContentService.getSubmissionResultForProgrammingContent(
            submissionId
          );
        setExecutionResult(result);
        if (
          result.status === SUBMISSION_STATUSES.PROCESSING ||
          result.status === SUBMISSION_STATUSES.IN_QUEUE
        ) {
          pollingRef.current = setTimeout(checkStatus, 1000);
        } else {
          setIsPolling(false);
          setIsRunning(false);
          setOutput("");
          clearTimeout(pollingRef.current);
        }
      } catch (error) {
        setOutput(`Polling Error: ${error.message}`);
        setIsPolling(false);
        setIsRunning(false);
        clearTimeout(pollingRef.current);
      }
    };
    checkStatus();
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, []);

  const handleConfirmReset = () => {
    isResetting.current = true;
    const activeDetails = content?.contentDetails.filter((d) => d.active) || [];
    const languageKey =
      activeDetails[0]?.problemCodeSnippets[0]?.programmingLanguage || "JAVA";
    const config = codeConstants[languageKey];
    setCode(config?.defaultCodesnippet || "");
    setEditorLanguage(config?.language || "text");
    setOutput("");
    setInput("");
    setExecutionResult(null);
  };

  const handleOpenDialog = () => {
    if (!openDialog && !isResetting.current) {
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    isResetting.current = false;
  };

  const handleIOTabChange = (event, newValue) => {
    setIOTabValue(newValue);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAddProblem = useCallback(() => {
    navigate(`${ROUTES.ADMIN_PROGRAMMING_CONTENT_ADD}`);
  }, [navigate]);

  const handleUpdateProblem = useCallback(async () => {
    if (content?.id) {
      try {
        const updatedContent =
          await programmingContentService.updateProgrammingContent(content);
        navigate(
          `${BASE_PATHS.ADMIN_PROGRAMMING_CONTENT}/${SUB_PATHS.UPDATE_CONTENT}/${content.id}`,
          { state: { content: updatedContent } }
        );
      } catch (error) {
        console.error("Failed to fetch updated content:", error);
        navigate(
          `${BASE_PATHS.ADMIN_PROGRAMMING_CONTENT}/${SUB_PATHS.UPDATE_CONTENT}/${content.id}`,
          { state: { content } }
        );
      }
    }
  }, [navigate, content]);

  const handleAction = useCallback(
    (action) => {
      switch (action.id) {
        case "back":
          handleBack();
          break;
        case "add":
          handleAddProblem();
          break;
        case "update":
          handleUpdateProblem();
          break;
        default:
          console.log("Unknown action:", action);
      }
    },
    [handleBack, handleAddProblem, handleUpdateProblem]
  );

  const outputTabs = [
    { icon: <InputIcon />, label: "Input" },
    { icon: <TerminalIcon />, label: "Output" },
  ];

  const outputContent = [
    <Div
      sx={{
        height: "100%",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TextField
        fullWidth
        multiline
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Enter input here..."
        variant="outlined"
        sx={{
          flexGrow: 1,
          "& .MuiInputBase-root": {
            height: "100%",
            alignItems: "flex-start",
            marginBottom: "2px",
          },
          "& .MuiInputBase-input": {
            overflowY: "auto",
          },
        }}
      />
    </Div>,
    <Div
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflowY: "auto",
        boxSizing: "border-box",
        minHeight: 200,
      }}
    >
      {isPolling ? (
        <Div
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            minWidth: 0,
            alignContent: "center",
            height: "100%",
          }}
        >
          <LoadingView message={loadingMessage} />
        </Div>
      ) : output ? (
        <Typography
          sx={{
            color: "error.main",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {output}
        </Typography>
      ) : executionResult ? (
        <Alert
          severity={
            executionResult.status === SUBMISSION_STATUSES.ACCEPTED
              ? ALERT_SEVERITIES.SUCCESS
              : ALERT_SEVERITIES.ERROR
          }
        >
          <AlertTitle>
            {metadata?.submissionStatus?.find(
              (s) => s.key === executionResult.status
            )?.value || executionResult.status}
          </AlertTitle>
          {executionResult.consoleOutput && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Console Output:
              </Typography>
              {executionResult.consoleOutput.split("\n").map((line, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          )}
          {executionResult.consoleError && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Console Error:
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {executionResult.consoleError}
              </Typography>
            </Box>
          )}
          {executionResult.compilationOutput && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Compilation Output:
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {executionResult.compilationOutput}
              </Typography>
            </Box>
          )}
          {!executionResult.consoleOutput &&
            !executionResult.consoleError &&
            !executionResult.compilationOutput && (
              <Typography variant="body2">
                No output, error, or compilation messages available.
              </Typography>
            )}
        </Alert>
      ) : (
        <Typography
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          Run your code to see the output!
        </Typography>
      )}
    </Div>,
  ];

  const codeTabs = [{ icon: <CodeIcon />, label: "Code", content: null }];

  const headerControls = (
    <EditorControls
      onRun={handleRun}
      onReset={handleOpenDialog}
      isRunning={isRunning}
      setOutputTabValue={setIOTabValue}
    />
  );

  const MobileLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "auto",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ flexShrink: 0, overflow: "auto", minHeight: 200 }}>
        <ContentPanel
          content={content}
          codeTabValue={codeTabValue}
          setCodeTabValue={setCodeTabValue}
          setCode={setCode}
          setEditorLanguage={setEditorLanguage}
        />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          minHeight: 400,
        }}
      >
        <MobileEditorPanel
          code={code}
          setCode={setCode}
          editorLanguage={editorLanguage}
          headerControls={headerControls}
          codeTabs={codeTabs}
          outputTabs={outputTabs}
          outputContent={outputContent}
          theme={{ mode: "light" }}
          codeTabValue={codeTabValue}
          setCodeTabValue={setCodeTabValue}
          ioTabValue={ioTabValue}
          setOutputTabValue={setIOTabValue}
          handleIOTabChange={handleIOTabChange}
        />
      </Box>
    </Box>
  );

  const DesktopLayout = () => (
    <PanelGroup
      direction="horizontal"
      style={{ width: "100%", height: "100%", boxSizing: "border-box" }}
    >
      <Panel defaultSize={50} minSize={20}>
        <ContentPanel
          content={content}
          codeTabValue={codeTabValue}
          setCodeTabValue={setCodeTabValue}
          setCode={setCode}
          setEditorLanguage={setEditorLanguage}
        />
      </Panel>
      <StyledPanelResizeHandle direction="vertical" />
      <Panel defaultSize={50} minSize={20}>
        <EditorPanel
          code={code}
          setCode={setCode}
          editorLanguage={editorLanguage}
          headerControls={headerControls}
          codeTabs={codeTabs}
          outputTabs={outputTabs}
          outputContent={outputContent}
          theme={{ mode: "light" }}
          codeTabValue={codeTabValue}
          setCodeTabValue={setCodeTabValue}
          ioTabValue={ioTabValue}
          setOutputTabValue={setIOTabValue}
          handleIOTabChange={handleIOTabChange}
        />
      </Panel>
    </PanelGroup>
  );

  return (
    <JumboCardQuick
      title={
        <ContentHeader
          isAdmin={isAdmin}
          onAction={handleAction}
          allowedActions={isAdmin ? ["back", "add", "update"] : []}
        />
      }
      headerSx={{
        borderBottom: 1,
        borderColor: "divider",
        "& .MuiCardHeader-action": { my: -0.75 },
      }}
      wrapperSx={{
        p: 0,
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "& .MuiCardContent-root:last-child": {
          paddingBottom: 0,
        },
      }}
    >
      {loading ? (
        <LoadingView message="Content Loading..." />
      ) : error ? (
        <ErrorView
          error={error}
          onBack={() => navigate(-1)}
          backButtonLabel="Back"
        />
      ) : content ? (
        <>{isMobile ? <MobileLayout /> : <DesktopLayout />}</>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            p: 3,
          }}
        >
          <Typography color="error">No content data available.</Typography>
        </Box>
      )}
      <JumboConfirmDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmReset}
        title="Are you sure?"
        message="Resetting will replace your current code with the default template."
        cancelText="Cancel"
        confirmText="Confirm"
        cancelColor="error"
        confirmColor="success"
        confirmVariant="contained"
      />
    </JumboCardQuick>
  );
};

export default ProgrammingContentPage;
